import type {
  BetSlipErrorResponse,
  BetSlipItem,
  BetSlipResponse,
  MultiInvestment,
} from '@mobi/betslip/types'
import { mapResponse } from '@mobi/betslip/Store/Bets/helpers/apiResponse/mapItemsWithResponse'
import { roundDownDecimal } from '@mobi/betslip/helpers/calculator/misc'
import { calculateMultiReturn } from '@mobi/betslip/helpers/calculator/multis'
import {
  BetRequest,
  proposeBet,
  confirmBet,
  isMysteryCommitResponse,
  MysteryCommitResponse,
  ToteBetResponse,
  FobCommitResponse,
  FobSingleErrorResponse,
} from '@core/Data/betting'
import {
  ConfirmBetFailed,
  ConfirmBetSucceeded,
  ProposeBetFailed,
  ProposeBetSucceeded,
  ProposeBet,
  ConfirmBet,
  ConfirmBetslipBetsFailed,
  ConfirmBetslipBetsSucceeded,
  ProposeBetslipBetsSucceeded,
  SetBonusBet,
  ToggleBonusBetUsage,
  ProposeMultiBetSucceeded,
  ProposeMultiBetFailed,
  ConfirmMultiBetSucceeded,
  ConfirmMultiBetFailed,
  MultiBetResponsePriceChange,
} from './signals'
import { UpdateAccountBalance } from '@core/State/UserAccount/userAccountDriver'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { isBetslipResponses } from '@core/Data/betslip'
import { postAllBetsToServer } from '@core/Areas/BetSlipNew/helpers/postAllBetsToServer'
import { getMultibetErrorDescription } from '@core/Areas/Betslip/helpers/state'
import { trackOptimoveEvent } from '@core/Services/Optimove/optimove'
import { shouldShowPriceChangeMessage } from './helpers/state'
import { QuickbetState, state$ as quickbetState$ } from './driver'

export async function proposeBetAsync(request: BetRequest): Promise<number | undefined> {
  ProposeBet()
  try {
    const response = await proposeBet(request)
    if (!response) return undefined

    if (Array.isArray(response)) return handleBetslipProposeResponses(response)

    response.accountBalance && UpdateAccountBalance({ balance: response.accountBalance })
    ProposeBetSucceeded(response)

    return response.accountBalance
  } catch (error) {
    ProposeBetFailed(error as FobSingleErrorResponse)
    throw error
  }
}

export async function confirmBetAsync(request: BetRequest): Promise<void> {
  ConfirmBet()
  try {
    const response = await confirmBet(request)
    if (response) {
      isBetslipResponses(response)
        ? handleBetslipCommitResponses(response)
        : handleQuickbetResponse(response)
    }
  } catch (err) {
    const error = err as FobSingleErrorResponse
    if (error.response && error.response.accountBalance) {
      UpdateAccountBalance({ balance: error.response.accountBalance })
    }
    ConfirmBetFailed(error)
  }
}

// ===================
// Multi Bet Placement
// ===================

export async function proposeMultiBetAsync({ items, investment }: MultiBetParams): Promise<void> {
  ProposeBet()
  try {
    const response = await postAllBetsToServer('propose', {
      items,
      multiInvestment: { ...investment, f1: 0, f2: 0, f3: 0, f4: 0, f5: 0 },
      multiBetError: null,
    })
    quickbetState$.take(1).subscribe(quickbetStateRecord => {
      const state = quickbetStateRecord.toJS() as QuickbetState
      const currentMultiItems = state.selectionMultiLegs
      const newMultiItems = state.selectionMultiLegs?.map(item =>
        mapResponse(item, response, response[0])
      )

      if (!currentMultiItems || !newMultiItems) throw new Error('No Legs Selected for Multi')

      if (response[0].success) {
        if (shouldShowMultiPriceChange(currentMultiItems, newMultiItems)) {
          MultiBetResponsePriceChange(newMultiItems)
          return
        }

        ProposeMultiBetSucceeded(newMultiItems)
        return
      }

      const multiBetError = getMultibetErrorDescription(response[0])
      const errorResponse = multiBetError
        ? {
            response: { message: multiBetError.errorMessage },
            code: multiBetError.betErrorType,
          }
        : undefined
      ProposeMultiBetFailed(errorResponse)
    })
  } catch (error) {
    ProposeMultiBetFailed(error as BetSlipErrorResponse)
  }
}

export async function confirmMultiBetAsync({
  items,
  investment,
  copiedFollowMePostId,
}: MultiBetParams): Promise<void> {
  ConfirmBet()
  try {
    const response = await postAllBetsToServer(
      'confirm',
      {
        items,
        multiInvestment: { ...investment, f1: 0, f2: 0, f3: 0, f4: 0, f5: 0 },
        multiBetError: null,
      },
      { multiFollowMePostId: copiedFollowMePostId }
    )
    quickbetState$.take(1).subscribe(quickbetStateRecord => {
      const state = quickbetStateRecord.toJS() as QuickbetState
      const currentMultiItems = state.selectionMultiLegs
      const newMultiItems = state.selectionMultiLegs?.map(item =>
        mapResponse(item, response, response[0])
      )

      if (!currentMultiItems || !newMultiItems) throw new Error('No Legs Selected for Multi')

      if (response[0].success) {
        ConfirmMultiBetSucceeded(response)

        if (response[0].receipt?.campaignActivatedInd) {
          fetchCampaignsAsync()
          trackOptimoveEvent({ eventName: 'redeemed_bonus_offer' })
        }

        if (response[0].receipt?.accountBalance) {
          UpdateAccountBalance({ balance: response[0].receipt.accountBalance })
        }

        SetBonusBet(null)
        ToggleBonusBetUsage(false)
        return
      }

      if (shouldShowMultiPriceChange(currentMultiItems, newMultiItems)) {
        MultiBetResponsePriceChange(newMultiItems)
        return
      }

      const multiBetError = getMultibetErrorDescription(response[0])
      const errorResponse: BetSlipErrorResponse | undefined = multiBetError
        ? {
            response: { message: multiBetError.errorMessage },
            code: multiBetError.betErrorType,
          }
        : undefined
      ConfirmMultiBetFailed(errorResponse)
    })
  } catch (error) {
    ConfirmMultiBetFailed(error as BetSlipErrorResponse)
  }
}

// =============
// Local Helpers
// =============

function handleBetslipProposeResponses(response: BetSlipResponse[]) {
  const accountBalance = response[0]?.accountBalance
  accountBalance && UpdateAccountBalance({ balance: accountBalance })

  ProposeBetslipBetsSucceeded()
  return accountBalance
}

function handleBetslipCommitResponses(response: BetSlipResponse[]) {
  const successfulResponses = response.filter(r => r.success)

  // all failed
  if (successfulResponses.length === 0) {
    const errorResponses = response.flatMap(r => (r.error ? [r.error] : []))
    ConfirmBetslipBetsFailed(errorResponses)
    return
  }

  const successfulResponsesByDateDescending = successfulResponses.sort(
    (a, b) =>
      new Date(b.receipt?.betPlacedTime ?? 0).getTime() -
      new Date(a.receipt?.betPlacedTime ?? 0).getTime()
  )

  const lastReceipt = successfulResponsesByDateDescending[0].receipt
  if (lastReceipt) {
    UpdateAccountBalance({
      balance: lastReceipt.accountBalance,
    })
    if (lastReceipt.campaignActivatedInd) {
      fetchCampaignsAsync()
      trackOptimoveEvent({ eventName: 'redeemed_bonus_offer' })
    }
  }

  ConfirmBetslipBetsSucceeded(response)
}

function handleQuickbetResponse(
  response: ToteBetResponse | MysteryCommitResponse | FobCommitResponse
) {
  if (response.accountBalance !== undefined) {
    UpdateAccountBalance({ balance: response.accountBalance })
  }

  if (isMysteryCommitResponse(response)) {
    response = buildMysteryConfirmBetSucceededResponse(response as MysteryCommitResponse)
  }

  ConfirmBetSucceeded(response)
  if (response.campaignActivatedInd) {
    fetchCampaignsAsync()
    trackOptimoveEvent({ eventName: 'redeemed_bonus_offer' })
  }

  SetBonusBet(null)
  ToggleBonusBetUsage(false)
}

function buildMysteryConfirmBetSucceededResponse(
  mysteryResponse: MysteryCommitResponse
): ToteBetResponse {
  const bet = mysteryResponse.bets[0]
  bet.accountBalance = mysteryResponse.accountBalance
  bet.campaignActivatedInd = mysteryResponse.campaignActivatedInd

  return bet
}

function shouldShowMultiPriceChange(currentItems: BetSlipItem[], newItems: BetSlipItem[]) {
  const currentPrice = roundDownDecimal(calculateMultiReturn(currentItems, { shouldRound: true }))
  const newPrice = roundDownDecimal(calculateMultiReturn(newItems, { shouldRound: true }))
  return shouldShowPriceChangeMessage({ winPrice: currentPrice }, { winPrice: newPrice })
}

// =====
// Types
// =====

type MultiBetParams = {
  items: BetSlipItem[]
  investment: Pick<MultiInvestment, 'value' | 'bonusBetId' | 'isBonusBet' | 'isBonusCash'>
  copiedFollowMePostId?: string
}
